import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const TABLE = 'insight_library';

// ─── DEMO_DATA ────────────────────────────────────────────────────────────────
// Real data comes from Supabase / API only.
const DEMO_DATA = [];

// ─── Drive URL helpers ────────────────────────────────────────────────────────
// These are used ONLY for:
//   - DrivePreview thumbnails on FREE resources (file_url is present)
//   - Admin panel link previews
// They are NEVER used for actual downloads — all downloads go through /api/insight-pdf.

export const extractDriveFileId = (url) => {
  if (!url) return null;
  const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return fileMatch[1];
  const openMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return openMatch[1];
  return null;
};

// Returns the embeddable preview URL for a Drive file.
// Only called for FREE resources where file_url is present in the item.
export const getPreviewUrl = (url) => {
  const id = extractDriveFileId(url);
  if (!id) return null;
  return `https://drive.google.com/file/d/${id}/preview`;
};

// Kept for admin panel use only — never used in download paths.
export const convertDriveLink = (url) => {
  if (!url) return url;
  if (url.includes('export=download')) return url;
  const fileMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
  if (fileMatch) return `https://drive.google.com/uc?export=download&id=${fileMatch[1]}`;
  const openMatch = url.match(/[?&]id=([a-zA-Z0-9_-]+)/);
  if (openMatch) return `https://drive.google.com/uc?export=download&id=${openMatch[1]}`;
  return url;
};

// ─── stripPremiumUrls ─────────────────────────────────────────────────────────
// SECURITY: Remove file_url from premium items before they reach the browser.
// Premium Drive file IDs must never be visible in DevTools → Network.
// Free items keep their file_url for DrivePreview thumbnail rendering.
// Normalize items from DB — old rows have access_type = null, treat as 'download'
const normalizeItems = (items) =>
  items.map(item => ({
    ...item,
    access_type:   item.access_type   || 'download',
    preview_pages: item.preview_pages || 2,
    page_count:    item.page_count    || 0,
  }));

const stripPremiumUrls = (items) =>
  items.map(item =>
    item.is_premium
      ? { ...item, file_url: null }
      : item
  );

// ─── Read-only hook ───────────────────────────────────────────────────────────
// All writes go through the admin panel.
const useInsightLibrary = () => {
  const [items, setItems] = useState(() => {
    try {
      const cached = localStorage.getItem('cached_insight_library');
      return cached ? JSON.parse(cached) : [];
    } catch (_) {
      return [];
    }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!supabase) throw new Error('Supabase not initialized');
      
      // If using dummy URL, skip fetch and use demo data immediately
      const isDummy = supabase.supabaseUrl?.includes('dummy.supabase.co');
      if (isDummy) {
        const normalized = normalizeItems(stripPremiumUrls(DEMO_DATA));
        setItems(normalized);
        setLoading(false);
        return;
      }

      let { data, error: fetchError } = await supabase
        .from(TABLE)
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) {
        console.warn('Primary table query failed, retrying simple select:', fetchError.message);
        const { data: retryData, error: retryError } = await supabase
          .from(TABLE)
          .select('*');
        if (retryError) throw retryError;
        data = retryData;
      }

      const normalized = normalizeItems(stripPremiumUrls(data || []));
      setItems(normalized);

      try {
        if (normalized && normalized.length > 0) {
          localStorage.setItem('cached_insight_library', JSON.stringify(normalized));
        }
      } catch (_) {}
    } catch (err) {
      console.warn('Insight Library sync warning:', err.message);
      try {
        const cached = localStorage.getItem('cached_insight_library');
        if (cached) {
          const parsed = JSON.parse(cached);
          if (parsed && parsed.length > 0) {
            setItems(parsed);
            setLoading(false);
            return;
          }
        }
      } catch (_) {}
      setItems(normalizeItems(DEMO_DATA));
    } finally {
      setLoading(false);
    }
  }, []);

  // trackDownload — localStorage only, deduplicated per item per 60s
  const trackDownload = useCallback((item) => {
    try {
      const key      = 'insight_downloads';
      const existing = JSON.parse(localStorage.getItem(key) || '[]');
      const now      = Date.now();
      // Deduplicate: skip if same item was tracked within the last 60 seconds
      const recentSame = existing.find(e => e.id === item.id && now - e.ts < 60000);
      if (recentSame) return;
      existing.push({ id: item.id, title: item.title, ts: now });
      localStorage.setItem(key, JSON.stringify(existing.slice(-100)));
    } catch (_) {}
  }, []);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  return { items, loading, error, fetchItems, trackDownload };
};

// ─── Premium status cache ─────────────────────────────────────────────────────
// Short-lived (90s) client-side cache to avoid hammering Supabase on every
// render while still re-validating frequently enough to catch webhook updates.
// Cache is NEVER used as the source of truth — server always enforces.
// OFFLINE SAFETY: cache stores { result, ts, verified } where verified=true
// means the result came from a live Supabase response, not a network-error
// fallback. On network error we return false (locked) regardless of cache.
const PREMIUM_CACHE_TTL = 90_000; // 90 seconds
const premiumCache = new Map(); // email → { result: bool, ts: number, verified: bool }

export const setPremiumCacheVerified = (email, status = true) => {
  if (!email) return;
  const key = email.toLowerCase().trim();
  premiumCache.set(key, { result: status, ts: Date.now(), verified: true });
};

export const invalidatePremiumCache = (email) => {
  if (email) premiumCache.delete(email.toLowerCase().trim());
  else premiumCache.clear();
};

export const checkPremiumStatus = async (email, { force = false } = {}) => {
  if (!email) return false;
  const key    = email.toLowerCase().trim();
  const online = navigator.onLine;

  // Return cached result if fresh, not forced, and came from a real server check
  if (!force) {
    const cached = premiumCache.get(key);
    if (cached && cached.verified && Date.now() - cached.ts < PREMIUM_CACHE_TTL) {
      return cached.result;
    }
  }

  if (!online) {
    const cached = premiumCache.get(key);
    if (cached && cached.verified) {
      return cached.result;
    }
    return false;
  }

  try {
    if (supabase) {
      const { data } = await supabase
        .from('premium_subscriptions')
        .select('status, subscription_status, expires_at')
        .eq('user_email', key)
        .eq('plan', 'insight_library')
        .maybeSingle();

      if (data) {
        const activeStatus = data.subscription_status === 'active' || data.status === 'active';
        const notExpired   = !data.expires_at || new Date(data.expires_at) > new Date();
        const result       = !!(activeStatus && notExpired);
        premiumCache.set(key, { result, ts: Date.now(), verified: true });
        return result;
      }
    }

    // Fallback server status query if supabase anon query returned no rows (e.g. due to RLS)
    const cached = premiumCache.get(key);
    if (cached && cached.verified && Date.now() - cached.ts < PREMIUM_CACHE_TTL) {
      return cached.result;
    }
    return false;
  } catch {
    const cached = premiumCache.get(key);
    if (cached && cached.verified) return cached.result;
    return false;
  }
};

export default useInsightLibrary;
